/**
 * Step 8: Interests and Community Engagement Preferences
 * Identifies specific interests and engagement preferences for personalized recommendations
 */

import React, { useEffect } from 'react';
import { Heart, Calendar, Users, MessageSquare, TrendingUp, CheckCircle } from 'lucide-react';
import { StepComponentProps } from '../../../../../types/onboarding';
import { InfoTooltip } from '../../../../shared/tooltip/InfoTooltip';
import { getTooltipContent } from '../../../../../constants/onboarding';
import { INTEREST_OPTIONS } from '../../../../../constants/onboarding/progressiveDisclosure';

export const Step8InterestsEngagement: React.FC<StepComponentProps> = ({
  formData,
  errors,
  onFieldChange,
  progressiveDisclosure,
}) => {
  const COMMUNITY_INTERESTS = [
    { label: 'Community Governance', tooltip: 'community-governance-interest' },
    { label: 'Financial Management', tooltip: 'financial-management-interest' },
    { label: 'Event Planning', tooltip: 'event-planning-interest' },
    { label: 'Maintenance & Facilities', tooltip: 'maintenance-facilities-interest' },
    { label: 'Communication & Transparency', tooltip: 'communication-transparency-interest' },
    { label: 'Vendor Management', tooltip: 'vendor-management-interest' },
    { label: 'Resident Services', tooltip: 'resident-services-interest' },
    { label: 'Community Safety', tooltip: 'community-safety-interest' },
    { label: 'Environmental Initiatives', tooltip: 'environmental-initiatives-interest' },
    { label: 'Technology & Innovation', tooltip: 'technology-innovation-interest' },
  ];

  const EVENT_PREFERENCES = [
    { label: 'Educational workshops', tooltip: 'educational-workshops' },
    { label: 'Social gatherings', tooltip: 'social-gatherings' },
    { label: 'Group purchases/bulk buying', tooltip: 'group-purchases-bulk-buying' },
    { label: 'Vendor fairs & negotiations', tooltip: 'vendor-fairs-negotiations' },
    { label: 'Financial planning workshops', tooltip: 'financial-planning-workshops' },
    { label: 'Digital governance training', tooltip: 'digital-governance-training' },
    { label: 'Fitness & wellness activities', tooltip: 'fitness-wellness-activities' },
    { label: 'Cultural events', tooltip: 'cultural-events' },
    { label: 'Volunteer opportunities', tooltip: 'volunteer-opportunities' },
    { label: 'Business networking', tooltip: 'business-networking' },
    { label: 'Family-friendly activities', tooltip: 'family-friendly-activities' },
    { label: 'Seasonal celebrations', tooltip: 'seasonal-celebrations' },
  ];

  const handleInterestsChange = (interest: string, checked: boolean) => {
    const currentInterests = formData.interests || [];
    if (checked) {
      onFieldChange('interests', [...currentInterests, interest]);
    } else {
      onFieldChange(
        'interests',
        currentInterests.filter((i) => i !== interest),
      );
    }
  };

  // Handle progressive disclosure interest selection
  const handleProgressiveInterestChange = (interestId: string, checked: boolean) => {
    // Update progressive disclosure
    progressiveDisclosure.handleInterestSelection(interestId, checked);

    // Also update form data to preserve selections
    const currentProgressiveInterests = (formData.progressiveInterests as string[]) || [];
    if (checked) {
      onFieldChange('progressiveInterests', [...currentProgressiveInterests, interestId]);
    } else {
      onFieldChange(
        'progressiveInterests',
        currentProgressiveInterests.filter((id) => id !== interestId),
      );
    }
  };

  // Analyze and set tier recommendation when interests change
  useEffect(() => {
    if (progressiveDisclosure.detectedInterests.length > 0) {
      const recommendedTier = progressiveDisclosure.analyzeAndRecommendTier();
      onFieldChange('recommendedTier', recommendedTier);
    }
  }, [progressiveDisclosure.detectedInterests.length]); // Only depend on the length to avoid infinite loops

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Heart className="w-8 h-8 text-pink-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Interests & Community Focus</h2>
        <p className="text-lg text-slate-600">
          Tell us about your community priorities to help us recommend the right package
        </p>
      </div>

      <div className="space-y-6">
        {/* Primary Focus Areas - Progressive Disclosure */}
        <div>
          <label className="block text-lg font-semibold text-slate-800 mb-3">
            Primary Focus Areas *
          </label>
          <p className="text-sm text-slate-600 mb-4">
            Select your top priorities for community improvement. This helps us recommend the right
            package for you.
          </p>
          <div className="grid grid-cols-1 gap-4">
            {INTEREST_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isSelected = progressiveDisclosure.detectedInterests.includes(option.id);
              const tierColor =
                option.tier === 'silver'
                  ? 'border-gray-300 hover:border-gray-400'
                  : option.tier === 'gold'
                    ? 'border-yellow-300 hover:border-yellow-400'
                    : 'border-purple-300 hover:border-purple-400';
              const tierBg =
                option.tier === 'silver'
                  ? 'bg-gray-50'
                  : option.tier === 'gold'
                    ? 'bg-yellow-50'
                    : 'bg-purple-50';

              return (
                <label
                  key={option.id}
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    isSelected
                      ? `${tierBg} ${tierColor.replace('hover:', '')} shadow-md`
                      : `border-slate-200 hover:bg-slate-50 ${tierColor}`
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => handleProgressiveInterestChange(option.id, e.target.checked)}
                    className="mr-4 text-pink-600 focus:ring-pink-500 w-4 h-4"
                  />
                  <div className="flex items-center space-x-3 flex-1">
                    <div
                      className={`p-2 rounded-full ${
                        option.tier === 'silver'
                          ? 'bg-gray-100'
                          : option.tier === 'gold'
                            ? 'bg-yellow-100'
                            : 'bg-purple-100'
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 ${
                          option.tier === 'silver'
                            ? 'text-gray-600'
                            : option.tier === 'gold'
                              ? 'text-yellow-600'
                              : 'text-purple-600'
                        }`}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-slate-800">{option.label}</div>
                        <InfoTooltip
                          title={
                            getTooltipContent(option.id)?.title || `${option.label} Information`
                          }
                          content={
                            getTooltipContent(option.id)?.content ||
                            `Learn more about ${option.label}`
                          }
                          iconSize={14}
                        />
                      </div>
                      <div
                        className={`text-xs font-medium mt-1 px-2 py-1 rounded-full inline-block ${
                          option.tier === 'silver'
                            ? 'bg-gray-200 text-gray-700'
                            : option.tier === 'gold'
                              ? 'bg-yellow-200 text-yellow-700'
                              : 'bg-purple-200 text-purple-700'
                        }`}
                      >
                        {option.tier.charAt(0).toUpperCase() + option.tier.slice(1)} Package
                      </div>
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
          {errors.progressiveInterests && (
            <p className="mt-1 text-sm text-red-600">{errors.progressiveInterests}</p>
          )}
        </div>

        {/* Secondary Community Interests */}
        <div>
          <label className="flex items-center gap-2 text-lg font-semibold text-slate-800 mb-3">
            Additional Community Interests
            <InfoTooltip
              title={
                getTooltipContent('additional-community-interests')?.title ||
                'Additional Community Interests'
              }
              content={
                getTooltipContent('additional-community-interests')?.content ||
                'Additional community interests and activities'
              }
              iconSize={16}
            />
          </label>
          <p className="text-sm text-slate-600 mb-4">
            What other aspects of community life interest you?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {COMMUNITY_INTERESTS.map((option) => (
              <label
                key={option.label}
                className="flex items-center p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.interests?.includes(option.label) || false}
                  onChange={(e) => handleInterestsChange(option.label, e.target.checked)}
                  className="mr-3 text-pink-600 focus:ring-pink-500"
                />
                <span className="text-slate-700 flex-1">{option.label}</span>
                <InfoTooltip
                  title={getTooltipContent(option.tooltip)?.title || option.label}
                  content={getTooltipContent(option.tooltip)?.content || option.label}
                  iconSize={14}
                />
              </label>
            ))}
          </div>
        </div>

        {/* Communication Preferences */}
        <div>
          <label className="flex items-center gap-2 text-lg font-semibold text-slate-800 mb-3">
            Communication Preferences *
            <InfoTooltip
              title={
                getTooltipContent('communication-preferences')?.title || 'Communication Preferences'
              }
              content={
                getTooltipContent('communication-preferences')?.content ||
                'How you prefer to receive community communications'
              }
              iconSize={16}
            />
          </label>
          <p className="text-sm text-slate-600 mb-4">
            How do you prefer to receive community updates and notifications?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Email notifications',
              'Mobile app push notifications',
              'SMS text messages',
              'In-app notifications only',
              'Physical mail/newsletters',
              'Community bulletin board',
            ].map((option) => (
              <label
                key={option}
                className="flex items-center p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.communicationPreferences?.includes(option) || false}
                  onChange={(e) => {
                    const current = formData.communicationPreferences || [];
                    if (e.target.checked) {
                      onFieldChange('communicationPreferences', [...current, option]);
                    } else {
                      onFieldChange(
                        'communicationPreferences',
                        current.filter((p) => p !== option),
                      );
                    }
                  }}
                  className="mr-3 text-pink-600 focus:ring-pink-500"
                />
                <span className="text-slate-700">{option}</span>
              </label>
            ))}
          </div>
          {errors.communicationPreferences && (
            <p className="mt-1 text-sm text-red-600">{errors.communicationPreferences}</p>
          )}
        </div>

        {/* Engagement Level */}
        <div>
          <label className="flex items-center gap-2 text-lg font-semibold text-slate-800 mb-3">
            Preferred Engagement Level *
            <InfoTooltip
              title="Engagement Levels Explained"
              content="This helps us understand how actively you want to participate in community activities and decision-making processes."
              iconSize={16}
            />
          </label>
          <p className="text-sm text-slate-600 mb-4">
            How actively do you want to participate in community activities?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              {
                value: 'observer',
                label: 'Observer',
                description: 'Stay informed but rarely participate actively',
                tooltip: 'engagement-observer',
              },
              {
                value: 'occasional',
                label: 'Occasional Participant',
                description: 'Join in when topics interest me',
                tooltip: 'engagement-occasional',
              },
              {
                value: 'regular',
                label: 'Regular Participant',
                description: 'Actively engage in discussions and events',
                tooltip: 'engagement-regular',
              },
              {
                value: 'leader',
                label: 'Community Leader',
                description: 'Help organize and lead community initiatives',
                tooltip: 'engagement-leader',
              },
            ].map((option) => (
              <label
                key={option.value}
                className="flex items-start p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="radio"
                  name="engagementLevel"
                  value={option.value}
                  checked={formData.engagementLevel === option.value}
                  onChange={(e) => onFieldChange('engagementLevel', e.target.value)}
                  className="mt-1 mr-3 text-pink-600 focus:ring-pink-500"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800">{option.label}</span>
                    {option.tooltip && (
                      <InfoTooltip
                        title={
                          getTooltipContent(option.tooltip)?.title || `${option.label} Information`
                        }
                        content={
                          getTooltipContent(option.tooltip)?.content ||
                          option.description ||
                          'Additional information'
                        }
                        iconSize={14}
                      />
                    )}
                  </div>
                  <div className="text-sm text-slate-600">{option.description}</div>
                </div>
              </label>
            ))}
          </div>
          {errors.engagementLevel && (
            <p className="mt-1 text-sm text-red-600">{errors.engagementLevel}</p>
          )}
        </div>

        {/* Event Preferences */}
        <div>
          <label className="flex items-center gap-2 text-lg font-semibold text-slate-800 mb-3">
            Event Preferences
            <InfoTooltip
              title={getTooltipContent('event-preferences')?.title || 'Event Preferences'}
              content={
                getTooltipContent('event-preferences')?.content ||
                'Types of community events you prefer'
              }
              iconSize={16}
            />
          </label>
          <p className="text-sm text-slate-600 mb-4">
            What types of community events and activities interest you most?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {EVENT_PREFERENCES.map((option) => (
              <label
                key={option.label}
                className="flex items-center p-3 border rounded-lg hover:bg-slate-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={formData.eventPreferences?.includes(option.label) || false}
                  onChange={(e) => {
                    const current = formData.eventPreferences || [];
                    if (e.target.checked) {
                      onFieldChange('eventPreferences', [...current, option.label]);
                    } else {
                      onFieldChange(
                        'eventPreferences',
                        current.filter((p) => p !== option.label),
                      );
                    }
                  }}
                  className="mr-3 text-pink-600 focus:ring-pink-500"
                />
                <span className="text-slate-700 flex-1">{option.label}</span>
                <InfoTooltip
                  title={getTooltipContent(option.tooltip)?.title || option.label}
                  content={getTooltipContent(option.tooltip)?.content || option.label}
                  iconSize={14}
                />
              </label>
            ))}
          </div>
        </div>

        {/* Additional Interests */}
        <div>
          <label
            htmlFor="additionalInterests"
            className="block text-lg font-semibold text-slate-800 mb-3"
          >
            Additional Interests & Ideas
          </label>
          <p className="text-sm text-slate-600 mb-4">
            Share any other interests or ideas for community engagement
          </p>
          <textarea
            id="additionalInterests"
            value={formData.additionalInterests || ''}
            onChange={(e) => onFieldChange('additionalInterests', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            placeholder="Tell us about any specific interests, hobby groups, or activities you'd like to see..."
          />
        </div>
      </div>

      {/* Information Panel */}
      <div className="bg-pink-50 rounded-lg p-6 mt-6">
        <div className="flex items-start space-x-3">
          <Users className="w-6 h-6 text-pink-600 mt-1" />
          <div>
            <h3 className="font-semibold text-pink-900 mb-2">Community Engagement Benefits</h3>
            <ul className="text-sm text-pink-800 space-y-1">
              <li>• Connect with like-minded neighbors and build relationships</li>
              <li>• Participate in events and activities tailored to your interests</li>
              <li>• Access to community forums and discussion groups</li>
              <li>• Opportunities to share skills and learn from others</li>
              <li>• Influence on community programs and initiatives</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
